import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useIsLoading from "../../../../hooks/useIsLoading";
import { Talent, TalentTraining } from "../../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentTraining,
} from "../../../talents/talentsSlice";
import { EditMode } from "../../TalentDetails";
import TrainingsForm from "./TrainingsForm";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type Props = {
  onClose: () => void;
  editMode: EditMode;
};

type TrainingFormValuesEdit = {
  type: string;
  completed: boolean;
  completedAt?: Date | null;
  validTo?: Date | null;
};

const TalentEditTraining = ({ onClose, editMode }: Props) => {
  const { t } = useTranslation();

  const { talentId } = useParams();
  const isLoading = useIsLoading(["talents"]);
  const dispatch = useAppDispatch();

  const talent = useAppSelector((state) =>
    selectTalentById(state, talentId!!),
  ) as Talent;

  const training = talent.trainings?.find(
    (training) => training.id === editMode.id,
  ) as TalentTraining;

  const formatDateString = (date?: Date) => {
    if (!date) return null;

    return format(date, "yyyy-MM-dd");
  };

  const defaultValues: TrainingFormValuesEdit = {
    type: training?.type,
    completed: training?.completed,
    completedAt: training.completedAt ? new Date(training.completedAt) : null,
    validTo: training.validTo ? new Date(training.validTo) : null,
  };

  const methods = useForm({ mode: "onChange", defaultValues });

  const onSave = (data: TrainingFormValuesEdit) => {
    // FIXME: create common utility function
    const parsedCompletedAt =
      data.completedAt && formatDateString(data.completedAt);
    const parsedValidTo = data.validTo && formatDateString(data.validTo);

    if (talentId) {
      const commandPayload = {
        id: editMode.id,
        type: data.type,
        completed: data.completed,
        version: training.version,
        ...(data.completed &&
          parsedCompletedAt && { completedAt: parsedCompletedAt }),
        ...(parsedValidTo && { validTo: parsedValidTo }),
      };

      dispatch(updateTalentTraining(commandPayload)).then((action) => {
        if (isFulfilled(action)) {
          onClose();
        }
        return isFulfilled(action);
      });
    }
  };

  const onCancel = () => {
    onClose();
  };

  const disabled =
    !methods.formState.isDirty ||
    !!Object.keys(methods.formState.errors).length ||
    isLoading;

  return (
    <FormProvider {...methods}>
      <DialogContentWrapper
        title={t("trainings:training")}
        margin
        onSave={methods.handleSubmit(onSave)}
        onCancel={onCancel}
        saveDisabled={disabled}
      >
        <OverlaySpinner isLoading={isLoading} />
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <TrainingsForm isEditMode />
        </UnsavedChangesProvider>
      </DialogContentWrapper>
    </FormProvider>
  );
};

export default TalentEditTraining;
